// src/components/company/CompanyDetail.tsx
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Building,
  Phone,
  Clock,
  ArrowLeft,
  Star,
  MapPin,
  ChevronRight,
  Sparkles,
  Scissors,
  Heart,
  Zap,
  Droplets,
  Sun,
  Wind,
  Leaf,
  HandMetal,
  Eye,
  Smile,
  Activity,
  Flower2,
  Waves,
  Brush,
  Flame,
  Tag,
  Timer,
  Circle,
  X,
} from 'lucide-react';
import { lucideIcons } from '@/lib/lucideIcons';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEmpresaBySlug } from '@/components/hooks/useEmpresa';
import type {
  ProductoEmpresa,
  HorarioEmpresa,
  CategoriaServicioEmpresa,
  ServicioEmpresa,
  FaseServicio,
  BonoEmpresa,
} from '@/components/hooks/useEmpresa';
import ProductDialog from '@/components/pages/dialogs/ProductDialog';
import Footer from '@/components/pages/shared/Footer';
import EmpresaLoader from '@/components/pages/shared/EmpresaLoader';
import { useTranslation } from 'react-i18next';
import CompanySEO from '@/components/seo/CompanySEO';
import EmpresaNoDisponible from '@/components/pages/EmpresaNoDisponible';
import { cookiesAceptadas, alCambiarCookies } from '@/lib/consentimientoCookies';

// ─── Precios ──────────────────────────────────────────────────────────────────

// Sin decimales cuando el precio es redondo, con dos cuando no. Antes se
// usaba toFixed(0) en todas partes y 27,50 se enseñaba como 28.
const formatPrecio = (valor: number) =>
  Number.isInteger(valor) ? String(valor) : valor.toFixed(2).replace('.', ',');

// ─── Types ────────────────────────────────────────────────────────────────────

interface RedSocial {
  id_tipo_red: string;
  nombre: string;
  icono?: string | null;
  url: string;
}

// ─── Social SVG icons ─────────────────────────────────────────────────────────

const SOCIAL_SVGS: Record<string, React.FC<{ className?: string }>> = {
  facebook: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  instagram: ({ className }) => (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  ),
  twitter: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  x: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  tiktok: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
    </svg>
  ),
  linkedin: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  youtube: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
      <polygon fill="white" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
    </svg>
  ),
  whatsapp: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  ),
  pinterest: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
    </svg>
  ),
};

const SocialIcon = ({
  icono,
  className,
}: {
  icono?: string | null;
  className?: string;
}) => {
  if (!icono) return null;
  const key = icono.toLowerCase().trim();
  const Svg = SOCIAL_SVGS[key];
  if (Svg) return <Svg className={className} />;
  return <LucideIcon name={icono} className={className} />;
};

// ─── Color helper ─────────────────────────────────────────────────────────────

const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ─── Schedule builder ─────────────────────────────────────────────────────────

const buildScheduleRows = (horario: HorarioEmpresa[]) => {
  const DIAS_ORDER = [
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
    'domingo',
  ];
  const DIAS_SHORT: Record<string, string> = {
    lunes: 'Lun',
    martes: 'Mar',
    miércoles: 'Mié',
    jueves: 'Jue',
    viernes: 'Vie',
    sábado: 'Sáb',
    domingo: 'Dom',
  };
  type Row = { label: string; slots: string[]; closed: boolean };
  const rows: Row[] = [];
  DIAS_ORDER.forEach((dia) => {
    const tramos = horario.filter(
      (h) => h.activo && h.dia_semana.toLowerCase() === dia
    );
    const closed = tramos.length === 0;
    const slots = tramos.map(
      (t) => `${t.hora_inicio.slice(0, 5)} – ${t.hora_fin.slice(0, 5)}`
    );
    const prev = rows[rows.length - 1];
    const sameSlots =
      prev &&
      !prev.closed &&
      !closed &&
      JSON.stringify(prev.slots) === JSON.stringify(slots);
    if (sameSlots) {
      const parts = prev.label.split(' – ');
      prev.label = `${parts[0]} – ${DIAS_SHORT[dia]}`;
    } else {
      rows.push({ label: DIAS_SHORT[dia], slots, closed });
    }
  });
  return rows;
};

const formatDuracion = (minutos: number): string => {
  if (minutos < 60) return `${minutos} MIN`;
  const dias = Math.floor(minutos / 1440);
  const horas = Math.floor((minutos % 1440) / 60);
  const mins = minutos % 60;
  const partes = [
    dias > 0 && `${dias}D`,
    horas > 0 && `${horas}H`,
    mins > 0 && `${mins}MIN`,
  ].filter(Boolean);
  return partes.join(' ');
};

// ─── ScrollToTop button ───────────────────────────────────────────────────────
const ScrollToTopButton = ({ primary }: { primary: string }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-5 z-50 w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
      style={{ backgroundColor: primary }}
      aria-label="Volver arriba"
    >
      <ChevronRight className="w-5 h-5 text-white -rotate-90" />
    </motion.button>
  );
};

const getServiceIcon = (nombre: string, descripcion?: string | null) => {
  const text = `${nombre} ${descripcion ?? ''}`.toLowerCase();
  if (/corte|pelo|cabello|hair|barba|barber|peluq/.test(text)) return Scissors;
  if (/facial|cara|limpieza|hidrat|piel|acné|peeling/.test(text))
    return Sparkles;
  if (/masaje|relax|tensión|espalda|cervical|corporal/.test(text)) return Waves;
  if (/depilac|laser|láser|cera|vello/.test(text)) return Zap;
  if (/manicura|pedicura|uña|nail/.test(text)) return HandMetal;
  if (/maquillaje|makeup|micropig|cejas|brow/.test(text)) return Brush;
  if (/bronceado|solar|tanning|sol/.test(text)) return Sun;
  if (/oxigen|vapor|vaporiz/.test(text)) return Wind;
  if (/botox|relleno|ácido|hialur|antiedad|lifting/.test(text)) return Activity;
  if (/ojos|ojeras|párpado|eye/.test(text)) return Eye;
  if (/drenaje|linfático|presot|circulac|hidro/.test(text)) return Droplets;
  if (/sonrisa|blanqueam|dental/.test(text)) return Smile;
  if (/aroma|aceite|esencial|flower/.test(text)) return Flower2;
  if (/cuerpo|body|wrap|envoltura|alga/.test(text)) return Leaf;
  if (/calor|termot|piedras|hot stone/.test(text)) return Flame;
  if (/corazón|bienestar|wellness|salud/.test(text)) return Heart;
  return Sparkles;
};

const PALETAS = [
  { gradientFrom: '#fdecea', gradientTo: '#f5c6cb', dotColor: '#e63950' },
  { gradientFrom: '#e8f0fd', gradientTo: '#c7d9f9', dotColor: '#3b82f6' },
  { gradientFrom: '#e8fdf5', gradientTo: '#b2f0d9', dotColor: '#10b981' },
  { gradientFrom: '#fef9e7', gradientTo: '#fde68a', dotColor: '#f59e0b' },
  { gradientFrom: '#f3e8ff', gradientTo: '#ddd6fe', dotColor: '#8b5cf6' },
];

const LucideIcon = ({
  name,
  className,
  style,
}: {
  name?: string | null;
  className?: string;
  style?: React.CSSProperties;
}) => {
  if (!name) return null;
  const pascalName = name
    .split(/[-_\s]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join('');
  const Icon = (lucideIcons as Record<string, any>)[pascalName];
  if (!Icon) return null;
  return <Icon className={className} style={style} />;
};

// ─── Social links component ───────────────────────────────────────────────────

const SocialLinks = ({
  redes,
  variant = 'hero',
  primary,
}: {
  redes: RedSocial[];
  variant?: 'hero' | 'contact';
  primary: string;
}) => {
  if (!redes?.length) return null;

  if (variant === 'hero') {
    return (
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        {redes.map((red) => (
          <a
            key={red.id_tipo_red}
            href={red.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={red.nombre}
            title={red.nombre}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:scale-110"
          >
            <SocialIcon icono={red.icono} className="w-4 h-4 text-white" />
          </a>
        ))}
      </div>
    );
  }

  return (
    <>
      {redes.map((red) => (
        <a
          key={red.id_tipo_red}
          href={red.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={red.nombre}
          className="inline-flex items-center gap-1.5 bg-[#f6f7f9] dark:bg-white/5 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200"
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
              hexToRgba(primary, 0.1);
            (e.currentTarget as HTMLAnchorElement).style.color = primary;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '';
            (e.currentTarget as HTMLAnchorElement).style.color = '';
          }}
        >
          <SocialIcon icono={red.icono} className="w-3.5 h-3.5 shrink-0" />
          {red.nombre}
        </a>
      ))}
    </>
  );
};

// ─── Fases del servicio ───────────────────────────────────────────────────────

const FasesServicio = ({
  fases,
  primary,
}: {
  fases: FaseServicio[];
  primary: string;
}) => {
  const [open, setOpen] = useState(false);
  const sorted = [...fases].sort((a, b) => a.orden - b.orden);
  const totalMinutos = sorted.reduce((acc, f) => acc + f.duracion_minutos, 0);

  return (
    <div className="mt-4 pl-12">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase hover:opacity-70 transition-opacity"
        style={{ color: primary }}
      >
        <Timer className="w-3.5 h-3.5" />
        Fases del servicio
        <span className="text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal ml-1">
          · {totalMinutos} min
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform duration-300 ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>

      {open && (
        <div className="relative mt-3">
          <div
            className="absolute left-[11px] top-4 bottom-4 w-px"
            style={{
              background: `linear-gradient(to bottom, ${hexToRgba(
                primary,
                0.3
              )}, ${hexToRgba(primary, 0.2)}, transparent)`,
            }}
          />
          <div className="space-y-2">
            {sorted.map((fase, idx) => (
              <motion.div
                key={fase.id_fase}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: idx * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="shrink-0">
                  {fase.requiere_atencion ? (
                    <div className="w-5 h-5 flex items-center justify-center">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: primary }}
                      />
                    </div>
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                  )}
                </div>
                <div className="flex-1 flex items-center justify-between gap-3 bg-[#f6f7f9] dark:bg-white/5 rounded-xl px-3 py-2">
                  <p className="text-sm font-medium text-[#0f172a] dark:text-white leading-tight">
                    {fase.nombre_fase}
                  </p>
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 shrink-0 tabular-nums">
                    {formatDuracion(fase.duracion_minutos)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Shared ───────────────────────────────────────────────────────────────────

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};
const getSectionBg = (idx: number) =>
  idx % 2 === 0
    ? 'bg-white dark:bg-black'
    : 'bg-[#f6f7f9] dark:bg-white/5';

// Compensa el header fixed (64px + fila de chips en móvil) para que
// scrollIntoView no deje el título tapado.
const SCROLL_MARGIN = 'scroll-mt-[106px] lg:scroll-mt-16';

// ─── Decor panel ──────────────────────────────────────────────────────────────

const DecorPanel = ({
  categoria,
  paleta,
  priceBadge,
  primary,
}: {
  categoria: CategoriaServicioEmpresa;
  paleta: (typeof PALETAS)[0];
  priceBadge?: string;
  primary: string;
}) => {
  const FallbackIcon = categoria.servicios?.[0]
    ? getServiceIcon(categoria.servicios[0].servicio)
    : Tag;
  return (
    <div className="block aspect-[16/7] md:aspect-[4/5] rounded-2xl md:rounded-3xl overflow-hidden relative">
      {' '}
      {categoria.imagen ? (
        <>
          <img
            src={categoria.imagen}
            alt={categoria.nombre}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${paleta.gradientFrom}, ${paleta.gradientTo})`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: `radial-gradient(circle, ${paleta.dotColor} 1.5px, transparent 1.5px)`,
              backgroundSize: '30px 30px',
            }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 rounded-full border-2 opacity-20"
            style={{ borderColor: paleta.dotColor }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border opacity-15"
            style={{ borderColor: paleta.dotColor }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            {categoria.icono ? (
              <LucideIcon
                name={categoria.icono}
                className="w-20 h-20 opacity-20"
                style={{ color: paleta.dotColor }}
              />
            ) : (
              <FallbackIcon
                className="w-20 h-20 opacity-20"
                style={{ color: paleta.dotColor }}
              />
            )}
          </div>
        </>
      )}
      <div className="hidden md:block absolute bottom-7 left-7 right-7 z-10 bg-white/85 dark:bg-black/85 backdrop-blur-sm rounded-2xl px-6 py-4">
        {categoria.nombre?.trim() &&
          categoria.nombre.trim() !== 'Sin categoría' && (
            <p
              className="text-[10px] uppercase tracking-[0.25em] mb-0.5"
              style={{ color: categoria.imagen ? primary : paleta.dotColor }}
            >
              {categoria.nombre}
            </p>
          )}
        {priceBadge && (
          <p className="text-2xl font-light text-[#0f172a] dark:text-white">
            Desde {priceBadge}€
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Service list ─────────────────────────────────────────────────────────────

const ServiceList = ({
  servicios,
  animDir,
  verMas,
  onToggleVerMas,
  maxVisible = 5,
  primary,
}: {
  servicios: ServicioEmpresa[];
  animDir: 'left' | 'right';
  verMas: boolean;
  onToggleVerMas: () => void;
  maxVisible?: number;
  primary: string;
}) => {
  const visibles = verMas ? servicios : servicios.slice(0, maxVisible);
  const hayMas = servicios.length > maxVisible;
  return (
    <div>
      <div className="space-y-0 divide-y divide-gray-100 dark:divide-white/10">
        {visibles.map((s, idx) => {
          const Icon = getServiceIcon(s.servicio, s.descripcion);
          const tieneFases = s.fases_servicio && s.fases_servicio.length > 0;
          const noReservable = s.reservable_online === false;
          const etiqueta = noReservable
            ? 'Consultar'
            : s.reserva_por_horas
            ? s.horas_min === s.horas_max
              ? `${s.horas_min} H`
              : `${s.horas_min}–${s.horas_max} H`
            : s.duracion_minutos
            ? formatDuracion(s.duracion_minutos)
            : 'Solicitar';
          const etiquetaDestacada =
            noReservable || (!s.reserva_por_horas && !s.duracion_minutos);
          return (
            <motion.div
              key={s.id_servicio}
              initial={{ opacity: 0, x: animDir === 'right' ? 24 : -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: idx * 0.08 }}
              className="group cursor-default py-7 sm:py-9 first:pt-0 last:pb-0"
            >
              <div className="flex justify-between items-start mb-2 sm:mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: hexToRgba(primary, 0.12) }}
                  >
                    {s.icono ? (
                      <LucideIcon
                        name={s.icono}
                        className="w-4 h-4"
                        style={{ color: primary }}
                      />
                    ) : (
                      <Icon className="w-4 h-4" style={{ color: primary }} />
                    )}
                  </div>
                  <h4 className="text-lg sm:text-xl lg:text-2xl font-light text-[#0f172a] dark:text-white leading-tight">
                    {s.servicio}
                  </h4>
                </div>
                <span
                  className="font-semibold text-[10px] sm:text-xs tracking-widest shrink-0 ml-3 mt-1"
                  style={
                    etiquetaDestacada
                      ? {
                          color: '#fff',
                          backgroundColor: primary,
                          padding: '2px 8px',
                          borderRadius: '9999px',
                        }
                      : { color: primary }
                  }
                >
                  {etiqueta}
                </span>
              </div>
              {s.descripcion && (
                <p className="text-gray-500 dark:text-gray-400 font-light leading-relaxed text-sm mb-4 pl-12">
                  {s.descripcion}
                </p>
              )}
              {s.precio !== undefined && s.precio !== null && (
                <div className="pl-12">
                  <span className="text-lg font-semibold text-[#0f172a] dark:text-white">
                    {formatPrecio(s.precio)}€{s.reserva_por_horas && '/h'}
                  </span>
                </div>
              )}
              {tieneFases && (
                <FasesServicio fases={s.fases_servicio!} primary={primary} />
              )}
            </motion.div>
          );
        })}
      </div>
      {hayMas && (
        <div className="flex justify-center mt-8">
          <button
            onClick={onToggleVerMas}
            className="flex items-center gap-2 text-sm font-semibold px-6 sm:px-8 py-3 rounded-full transition-all duration-300"
            style={{
              color: primary,
              border: `1px solid ${hexToRgba(primary, 0.3)}`,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor =
                primary;
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                hexToRgba(primary, 0.06);
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor =
                hexToRgba(primary, 0.3);
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'transparent';
            }}
          >
            {verMas ? 'Ver menos' : `Ver todos (${servicios.length})`}
            <ChevronRight
              className={`w-4 h-4 transition-transform duration-300 ${
                verMas ? 'rotate-90' : ''
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Review card ──────────────────────────────────────────────────────────────

const ReviewCard = ({
  v,
  idx,
  primary,
}: {
  v: any;
  idx: number;
  primary: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: idx * 0.05 }}
    className="bg-white dark:bg-white/5 rounded-3xl p-5 sm:p-6 border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
    itemScope
    itemType="https://schema.org/Review"
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < v.puntuacion
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-200 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
      <time
        className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0 tabular-nums"
        dateTime={v.fecha_valoracion?.slice(0, 10)}
        itemProp="datePublished"
      >
        {new Date(v.fecha_valoracion).toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </time>
    </div>
    {v.comentario && (
      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-5">
        <span
          className="text-lg leading-none mr-0.5 font-serif"
          style={{ color: primary }}
        >
          "
        </span>
        {v.comentario}
        <span
          className="text-lg leading-none ml-0.5 font-serif"
          style={{ color: primary }}
        >
          "
        </span>
      </p>
    )}
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-white/10">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: hexToRgba(primary, 0.15) }}
      >
        <span className="text-xs font-bold" style={{ color: primary }}>
          {v.nombre_cliente?.charAt(0).toUpperCase()}
        </span>
      </div>
      <span
        className="text-sm font-semibold text-[#0f172a] dark:text-white"
        itemProp="author"
        itemScope
        itemType="https://schema.org/Person"
      >
        <span itemProp="name">{v.nombre_cliente}</span>
      </span>
    </div>
  </motion.div>
);

// ─── Masonry grid helper ──────────────────────────────────────────────────────

const MasonryGrid = <T extends { id: string; src: string; alt: string }>({
  items,
  onClick,
  renderBadge,
  renderHoverInfo,
  variant = 'cover',
}: {
  items: T[];
  onClick: (item: T) => void;
  renderBadge?: (item: T) => React.ReactNode;
  renderHoverInfo?: (item: T) => React.ReactNode;
  variant?: 'cover' | 'contain';
}) => {
  const isContain = variant === 'contain';
  const imgFit = isContain
    ? 'object-contain p-3'
    : 'object-cover';
  const cellBg = isContain ? 'bg-gray-50 dark:bg-white/[0.04]' : '';

  const cols4: T[][] = [[], [], [], []];
  items.forEach((item, i) => cols4[i % 4].push(item));
  const cols2: T[][] = [[], []];
  items.forEach((item, i) => cols2[i % 2].push(item));

  const Cell = ({
    item,
    colIdx,
    itemIdx,
    className,
  }: {
    item: T;
    colIdx: number;
    itemIdx: number;
    className: string;
  }) => (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (colIdx * 3 + itemIdx) * 0.04 }}
      onClick={() => onClick(item)}
      className={`group relative overflow-hidden rounded-2xl cursor-pointer ${cellBg} ${className}`}
    >
      <img
        src={item.src}
        alt={item.alt}
        loading="lazy"
        className={`w-full h-full ${imgFit} transition-transform duration-700 ease-out group-hover:scale-110`}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-300" />
      {renderBadge?.(item)}
      {renderHoverInfo && (
        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          {renderHoverInfo(item)}
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
        <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
          <Eye className="w-4 h-4 text-[#0f172a]" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <>
      {/* Desktop: 4 cols masonry */}
      <div className="hidden lg:grid grid-cols-4 gap-3">
        {cols4.map((col, colIdx) => (
          <div
            key={colIdx}
            className={`flex flex-col gap-3 ${colIdx % 2 === 1 ? 'mt-8' : ''}`}
          >
            {col.map((item, itemIdx) => (
              <Cell
                key={item.id}
                item={item}
                colIdx={colIdx}
                itemIdx={itemIdx}
                className={
                  (colIdx + itemIdx) % 3 === 0
                    ? 'aspect-[3/4]'
                    : 'aspect-square'
                }
              />
            ))}
          </div>
        ))}
      </div>

      {/* Tablet: 2 cols */}
      <div className="hidden sm:grid lg:hidden grid-cols-2 gap-3">
        {cols2.map((col, colIdx) => (
          <div
            key={colIdx}
            className={`flex flex-col gap-3 ${colIdx === 1 ? 'mt-6' : ''}`}
          >
            {col.map((item, itemIdx) => (
              <Cell
                key={item.id}
                item={item}
                colIdx={colIdx}
                itemIdx={itemIdx}
                className={itemIdx % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Mobile: 2 cols */}
      <div className="grid sm:hidden grid-cols-2 gap-2">
        {items.map((item, idx) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: idx * 0.04 }}
            onClick={() => onClick(item)}
            className={`group relative aspect-square overflow-hidden rounded-xl cursor-pointer ${cellBg}`}
          >
            <img
              src={item.src}
              alt={item.alt}
              loading="lazy"
              className={`w-full h-full ${imgFit} transition-transform duration-700 group-hover:scale-105`}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300" />
            {renderBadge?.(item)}
          </motion.div>
        ))}
      </div>
    </>
  );
};

// ─── Bono card ────────────────────────────────────────────────────────────────

const BonoCard = ({
  bono,
  idx,
  primary,
}: {
  bono: BonoEmpresa;
  idx: number;
  primary: string;
}) => {
  const meses = Math.round(bono.duracion_dias / 30);
  const validezLabel =
    bono.duracion_dias % 30 === 0
      ? `${meses} ${meses === 1 ? 'mes' : 'meses'}`
      : `${bono.duracion_dias} días`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: idx * 0.07 }}
      className="group relative flex flex-col bg-white dark:bg-white/5 rounded-2xl sm:rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
    >
      {/* Accent bar */}
      <div
        className="h-1 w-full shrink-0"
        style={{
          background: `linear-gradient(90deg, ${primary}, ${hexToRgba(
            primary,
            0.4
          )})`,
        }}
      />

      <div className="flex flex-col flex-1 p-5 sm:p-6">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: hexToRgba(primary, 0.12) }}
          >
            <Tag className="w-4 h-4" style={{ color: primary }} />
          </div>
          <div
            className="text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1 rounded-full shrink-0"
            style={{
              color: primary,
              backgroundColor: hexToRgba(primary, 0.1),
            }}
          >
            {bono.cantidad_servicios} sesiones
          </div>
        </div>

        {/* Name & description */}
        <h3 className="text-base sm:text-lg font-semibold text-[#0f172a] dark:text-white leading-snug mb-1.5">
          {bono.nombre_bono}
        </h3>
        {bono.descripcion && (
          <p className="text-sm text-gray-500 dark:text-gray-400 font-light leading-relaxed flex-1 mb-4">
            {bono.descripcion}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-end justify-between gap-2 mt-auto pt-4 border-t border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>Válido {validezLabel}</span>
          </div>
          <p className="text-2xl font-light text-[#0f172a] dark:text-white tabular-nums">
            {formatPrecio(bono.precio_total)}
            <span className="text-base ml-0.5">€</span>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const CompanyDetail = () => {
  // Google Maps pone cookies de terceros: sin consentimiento, el mapa solo
  // se carga si el usuario lo pide para esta visita.
  const [mapaActivo, setMapaActivo] = useState(cookiesAceptadas);
  useEffect(
    () => alCambiarCookies(() => setMapaActivo((v) => v || cookiesAceptadas())),
    []
  );
  const { categoria_slug, ciudad_slug, empresa_slug } = useParams<{
    categoria_slug: string;
    ciudad_slug: string;
    empresa_slug: string;
  }>();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const canGoBack = window.history.state?.idx > 0;

  const { empresa, loadingEmpresa: loading, logoPreview } = useEmpresaBySlug(
    categoria_slug ?? '',
    ciudad_slug ?? '',
    empresa_slug ?? ''
  );

  const primary = empresa?.color_primario ?? '#e63950';
  const secondary = empresa?.color_secundario ?? '#103254';

  const [mostrarTodas, setMostrarTodas] = useState(false);
  const [productoActivo, setProductoActivo] = useState<ProductoEmpresa | null>(
    null
  );
  const [verTodasFotos, setVerTodasFotos] = useState(false);
  const [fotoActiva, setFotoActiva] = useState<{
    id_foto: string;
    url_foto: string;
    titulo?: string | null;
    destacado?: boolean;
  } | null>(null);
  const [verMasProductos, setVerMasProductos] = useState(false);
  const [verMasCat, setVerMasCat] = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState<string>('');

  // Al terminar de cargar, mostramos el logo de la empresa un instante antes
  // de descubrir la pantalla.
  const [splashDone, setSplashDone] = useState(false);
  const [logoCargado, setLogoCargado] = useState(false);
  useEffect(() => {
    if (!empresa) return;
    if (!empresa.logo) {
      setSplashDone(true);
      return;
    }
    if (!logoCargado) return;
    const id = setTimeout(() => setSplashDone(true), 800);
    return () => clearTimeout(id);
  }, [empresa, logoCargado]);

  // ── Derivadas "safe" (usan `empresa?.`) para poder declarar los hooks
  // SIEMPRE en el mismo orden, incluso antes de saber si `empresa` cargó. ──
  const categoriasSafe: CategoriaServicioEmpresa[] = useMemo(() => {
    if (!empresa) return [];
    return (empresa as any).categorias_servicios?.length > 0
      ? (empresa as any).categorias_servicios
      : empresa.servicios.length > 0
      ? [
          {
            id_categoria_servicio: null,
            nombre: 'Servicios',
            imagen: null,
            icono: null,
            orden: 0,
            servicios: empresa.servicios,
          },
        ]
      : [];
  }, [empresa]);

  const bonosActivosSafe = useMemo(
    () => empresa?.bonos.filter((b) => b.activo) ?? [],
    [empresa]
  );
  const hasProductosSafe = (empresa?.productos.length ?? 0) > 0;
  const hasBonosSafe = bonosActivosSafe.length > 0;
  const hasTrabajadoresSafe = (empresa?.trabajadores.length ?? 0) > 0;
  const hasFotosSafe = (empresa?.fotos.length ?? 0) > 0;
  const hasEncuentranosSafe = !!(
    empresa &&
    ([empresa.direccion, empresa.localidad, empresa.ciudad, empresa.pais].filter(
      Boolean
    ).length > 0 ||
      empresa.telefono ||
      empresa.email ||
      empresa.horario_empresa.length > 0)
  );

  const sectionIds = useMemo(() => {
    const ids: string[] = [];
    if (categoriasSafe.length > 0) ids.push('servicios');
    if (hasBonosSafe) ids.push('bonos');
    if (hasProductosSafe) ids.push('productos');
    if (hasTrabajadoresSafe) ids.push('equipo');
    if (hasFotosSafe) ids.push('galeria');
    if (hasEncuentranosSafe) ids.push('ubicacion');
    if ((empresa?.valoraciones_empresa?.length ?? 0) > 0) ids.push('valoraciones');
    return ids;
  }, [
    categoriasSafe,
    hasBonosSafe,
    hasProductosSafe,
    hasTrabajadoresSafe,
    hasFotosSafe,
    hasEncuentranosSafe,
    empresa,
  ]);

  useEffect(() => {
    if (sectionIds.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sectionIds]);

  const valoracionesVisibles = empresa?.valoraciones_empresa?.slice(0, 6) || [];
  const canonicalUrl = `${
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://bookoll.com'
  }/${categoria_slug}/${ciudad_slug}/${empresa_slug}`;

  if (loading || !empresa || !splashDone) {
    return (
      <EmpresaLoader
        logoUrl={empresa?.logo ?? logoPreview?.logo}
        nombre={empresa?.empresa ?? logoPreview?.empresa}
        onLogoLoad={() => setLogoCargado(true)}
        onLogoError={() => setSplashDone(true)}
      />
    );
  }

  // Empresa sin plan efectivo (ni promo ni contratado): no disponible.
  if ((empresa as any).tiene_plan_activo === false) {
    return <EmpresaNoDisponible />;
  }

  // A partir de aquí `empresa` ya está garantizado (TypeScript lo estrecha tras el guard de arriba).
  const categorias = categoriasSafe;
  const bonosActivos = bonosActivosSafe;
  const hasProductos = hasProductosSafe;
  const hasBonos = hasBonosSafe;
  const hasTrabajadores = hasTrabajadoresSafe;
  const hasFotos = hasFotosSafe;
  const hasEncuentranos = hasEncuentranosSafe;

  const redes: RedSocial[] = empresa.redes_sociales ?? [];
  const direccionCompleta = [
    empresa.direccion,
    empresa.localidad,
    empresa.ciudad,
    empresa.pais,
  ]
    .filter(Boolean)
    .join(', ');
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    direccionCompleta
  )}`;
  const fotosOrdenadas = [...empresa.fotos].sort((a, b) =>
    a.destacado === b.destacado ? 0 : a.destacado ? -1 : 1
  );
  const fotoDestacada = empresa.fotos.find((f) => f.destacado);
  const scheduleRows = buildScheduleRows(empresa.horario_empresa);
  const hasContacto = !!(empresa.telefono || empresa.email || redes.length > 0);

  const postCatOffset = {
    bonos: 0,
    productos: hasBonos ? 1 : 0,
    equipo: (hasBonos ? 1 : 0) + (hasProductos ? 1 : 0),
    galeria:
      (hasBonos ? 1 : 0) + (hasProductos ? 1 : 0) + (hasTrabajadores ? 1 : 0),
    ubicacion:
      (hasBonos ? 1 : 0) +
      (hasProductos ? 1 : 0) +
      (hasTrabajadores ? 1 : 0) +
      (hasFotos ? 1 : 0),
    valoraciones:
      (hasBonos ? 1 : 0) +
      (hasProductos ? 1 : 0) +
      (hasTrabajadores ? 1 : 0) +
      (hasFotos ? 1 : 0) +
      (hasEncuentranos ? 1 : 0),
  };

  const goToBooking = () =>
    navigate(`/client-booking/${empresa.id_empresa}`, {
      state: {
        showBackButton: true,
        logo: empresa.logo,
        nombre: empresa.empresa,
      },
    });

  const navLinkBase =
    'px-3 py-2 text-xs font-semibold tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 transition-colors rounded-full whitespace-nowrap';
  const handleNavEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.color = primary;
    e.currentTarget.style.backgroundColor = hexToRgba(primary, 0.06);
  };
  const handleNavLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.color = '';
    e.currentTarget.style.backgroundColor = '';
  };

  const scrollToId = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Chip móvil: resalta con el color de empresa (suave) cuando su sección está activa
  const renderMobileChip = (id: string, label: string) => {
    const isActive = activeSection === id;
    return (
      <a
        key={id}
        href={`#${id}`}
        onClick={scrollToId(id)}
        className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide rounded-full whitespace-nowrap shrink-0 transition-colors ${
          isActive
            ? ''
            : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5'
        }`}
        style={
          isActive
            ? { backgroundColor: hexToRgba(primary, 0.15), color: primary }
            : undefined
        }
      >
        {label}
      </a>
    );
  };

  const fotoItems = fotosOrdenadas.map((f) => ({
    id: f.id_foto,
    src: f.url_foto,
    alt: f.titulo || 'Foto',
    _orig: f,
  }));
  const productoItems = empresa.productos
    .filter((p) => !!p.imagen_principal)
    .map((p) => ({
      id: p.id_producto,
      src: p.imagen_principal!,
      alt: p.nombre,
      precio: p.precio,
      descripcion: p.descripcion,
      _orig: p,
    }));
  const productosVisibles = verMasProductos
    ? productoItems
    : productoItems.slice(0, 8);
  const fotosVisibles = verTodasFotos ? fotoItems : fotoItems.slice(0, 8);

  // ── HEADER: se renderiza vía portal directamente en document.body para
  // garantizar que `position: fixed` no se rompa por transforms de ancestros
  // (p.ej. animaciones de página con Framer Motion en el layout superior). ──
  const headerContent = (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-white dark:bg-black border-b border-gray-100 dark:border-white/10 shadow-sm"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-16 h-16 flex items-center gap-2 sm:gap-4">
        <button
          onClick={() => (canGoBack ? navigate(-1) : navigate('/'))}
          className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[#0f172a] dark:text-white" />
        </button>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0 hover:opacity-80 transition-opacity"
        >
          {empresa.logo ? (
            <img
              src={empresa.logo}
              alt={empresa.empresa}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border border-gray-200 dark:border-white/10 shrink-0"
            />
          ) : (
            <div
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: secondary }}
            >
              <Building className="w-5 h-5 sm:w-5 sm:h-5 text-white" />
            </div>
          )}
          <span
            className={`block font-bold text-sm sm:text-base text-[#0f172a] dark:text-white truncate ${
              empresa.reservas_online_citas
                ? 'max-w-[160px] sm:max-w-[260px] md:max-w-[400px]'
                : 'max-w-[200px] sm:max-w-[360px] md:max-w-[560px]'
            }`}
          >
            {empresa.empresa}
          </span>
        </button>
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {categorias.length > 0 && (
            <a
              href="#servicios"
              onClick={scrollToId('servicios')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Servicios
            </a>
          )}
          {hasBonos && (
            <a
              href="#bonos"
              onClick={scrollToId('bonos')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Bonos
            </a>
          )}
          {hasProductos && (
            <a
              href="#productos"
              onClick={scrollToId('productos')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Productos
            </a>
          )}
          {hasTrabajadores && (
            <a
              href="#equipo"
              onClick={scrollToId('equipo')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Equipo
            </a>
          )}
          {hasFotos && (
            <a
              href="#galeria"
              onClick={scrollToId('galeria')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Galería
            </a>
          )}
          {hasEncuentranos && (
            <a
              href="#ubicacion"
              onClick={scrollToId('ubicacion')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Ubicación
            </a>
          )}
          {(empresa.valoraciones_empresa?.length ?? 0) > 0 && (
            <a
              href="#valoraciones"
              onClick={scrollToId('valoraciones')}
              className={navLinkBase}
              onMouseEnter={handleNavEnter}
              onMouseLeave={handleNavLeave}
            >
              Valoraciones
            </a>
          )}
        </nav>
        <div className="ml-auto shrink-0 flex">
          {empresa.reservas_online_citas &&
            (empresa as any).plan_incluye_reservas && (
            <Button
              onClick={goToBooking}
              variant="ghost"
              className="text-[11px] sm:text-xs font-bold tracking-[0.1em] uppercase px-3 sm:px-3.5 h-8 sm:h-7 rounded-full shadow-sm transition-all duration-200 hover:opacity-90 hover:text-white"
              style={{ backgroundColor: primary, color: '#fff' }}
            >
              {t('companyDetail.bookNow')}
            </Button>
          )}
        </div>
      </div>

      {/* Nav rápida móvil: chips horizontales con scroll, saltan directamente a cada
          sección y se resaltan con el color de empresa según lo que esté visible */}
      <div className="lg:hidden border-t border-gray-100 dark:border-white/10 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1.5 px-3 py-2 w-max">
          {categorias.length > 0 && renderMobileChip('servicios', 'Servicios')}
          {hasBonos && renderMobileChip('bonos', 'Bonos')}
          {hasProductos && renderMobileChip('productos', 'Productos')}
          {hasTrabajadores && renderMobileChip('equipo', 'Equipo')}
          {hasFotos && renderMobileChip('galeria', 'Galería')}
          {hasEncuentranos && renderMobileChip('ubicacion', 'Ubicación')}
          {(empresa.valoraciones_empresa?.length ?? 0) > 0 &&
            renderMobileChip('valoraciones', 'Valoraciones')}
        </div>
      </div>
    </header>
  );

  return (
    <div className="min-h-screen flex flex-col w-full bg-white dark:bg-black">
      <CompanySEO
        empresa={empresa}
        canonicalUrl={canonicalUrl}
        ciudadSlug={ciudad_slug}
        categoriaSlug={categoria_slug}
        noIndex={!empresa?.activo || empresa?.privada === true}
      />

      {/* ── HEADER (portal a document.body para quedar siempre fijo) ──────── */}
      {typeof document !== 'undefined' &&
        createPortal(headerContent, document.body)}

      {/* Espaciadores: compensan la altura del header fixed.
          En móvil el header incluye la fila extra de chips. */}
      <div
        className="lg:hidden"
        style={{ height: 'calc(4rem + 42px + env(safe-area-inset-top))' }}
      />
      <div
        className="hidden lg:block"
        style={{ height: 'calc(4rem + env(safe-area-inset-top))' }}
      />

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <motion.section
        aria-label={`Información de ${empresa.empresa}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative text-white overflow-hidden min-h-[500px] sm:min-h-[580px]"
      >
        {fotoDestacada && (
          <div
            className="absolute inset-0 bg-center bg-cover"
            role="img"
            aria-label={`Foto de ${empresa.empresa}`}
            style={{ backgroundImage: `url(${fotoDestacada.url_foto})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-black/60" />
        <div className="relative z-10 min-h-[500px] sm:min-h-[580px] px-4 sm:px-6 flex flex-col items-center justify-center text-center py-12">
          <div className="flex flex-wrap items-center justify-center gap-2 max-w-full px-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              {empresa.empresa}
            </h1>
            {empresa.valoracion_media_empresa &&
              (empresa.valoraciones_empresa?.length ?? 0) > 0 && (
                <span className="flex items-center gap-1 mt-1 sm:mt-0">
                  <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                  <span className="font-semibold text-white text-sm">
                    {empresa.valoracion_media_empresa.toFixed(1)}
                  </span>
                </span>
              )}
          </div>
          {empresa.descripcion && (
            <p className="mt-3 max-w-xl sm:max-w-2xl text-base sm:text-lg lg:text-xl text-blue-100 dark:text-gray-300 px-2">
              {empresa.descripcion}
            </p>
          )}
          {empresa.ciudad && (
            <p className="mt-2 flex items-center gap-1 text-sm text-white/70">
              <MapPin className="w-3.5 h-3.5" />
              <span>
                {[empresa.localidad, empresa.ciudad].filter(Boolean).join(', ')}
              </span>
            </p>
          )}
          <SocialLinks redes={redes} variant="hero" primary={primary} />
        </div>
      </motion.section>

      {/* ── SERVICIOS ───────────────────────────────────────────────────────── */}
      {categorias.map((cat, catIdx) => {
        const paleta = PALETAS[catIdx % PALETAS.length];
        const bgClass = getSectionBg(catIdx);
        const panelRight = catIdx % 2 !== 0;
        const catKey = cat.id_categoria_servicio ?? `cat-${catIdx}`;
        const verMas = verMasCat[catKey] ?? false;
        const toggleVerMas = () =>
          setVerMasCat((prev) => ({ ...prev, [catKey]: !prev[catKey] }));
        const preciosValidos = cat.servicios
          .filter((s) => s.precio != null)
          .map((s) => s.precio!);
        const priceBadge =
          preciosValidos.length > 0
            ? formatPrecio(Math.min(...preciosValidos))
            : undefined;

        return (
          <section
            id={catIdx === 0 ? 'servicios' : undefined}
            key={catKey}
            aria-label={cat.nombre?.trim() ? cat.nombre : 'Servicios'}
            className={`${bgClass} ${
              catIdx === 0 ? SCROLL_MARGIN : ''
            } py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
          >
            <motion.div {...fadeUp} className="max-w-7xl mx-auto">
              {cat.nombre?.trim() && cat.nombre.trim() !== 'Sin categoría' && (
                <div className="mb-10 sm:mb-16">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                    {cat.nombre}
                  </h2>
                  <div
                    className="w-12 h-px mt-4 sm:mt-5"
                    style={{ backgroundColor: primary }}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-16 lg:gap-24 items-start">
                {panelRight ? (
                  <>
                    {cat.imagen && (
                      <div className="order-first md:order-last md:sticky md:top-32">
                        <DecorPanel
                          categoria={cat}
                          paleta={paleta}
                          priceBadge={priceBadge}
                          primary={primary}
                        />
                      </div>
                    )}
                    <ServiceList
                      servicios={cat.servicios}
                      animDir="left"
                      verMas={verMas}
                      onToggleVerMas={toggleVerMas}
                      primary={primary}
                    />
                  </>
                ) : (
                  <>
                    {cat.imagen && (
                      <div className="md:sticky md:top-32">
                        <DecorPanel
                          categoria={cat}
                          paleta={paleta}
                          priceBadge={priceBadge}
                          primary={primary}
                        />
                      </div>
                    )}
                    <ServiceList
                      servicios={cat.servicios}
                      animDir="right"
                      verMas={verMas}
                      onToggleVerMas={toggleVerMas}
                      primary={primary}
                    />
                  </>
                )}
              </div>
            </motion.div>
          </section>
        );
      })}

      {/* ── BONOS ───────────────────────────────────────────────────────────── */}
      {hasBonos && (
        <section
          id="bonos"
          aria-label="Bonos y packs"
          className={`${getSectionBg(
            categorias.length + postCatOffset.bonos
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8 sm:mb-12 gap-4 flex-wrap">
              <div>
                <p
                  className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2"
                  style={{ color: primary }}
                >
                  Ahorra más
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                  Bonos y Packs
                </h2>
                <div
                  className="w-12 h-px mt-4"
                  style={{ backgroundColor: primary }}
                />
              </div>
              <span className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-gray-500 font-medium">
                {bonosActivos.length} disponibles
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {bonosActivos.map((bono, idx) => (
                <BonoCard
                  key={bono.id_bono}
                  bono={bono}
                  idx={idx}
                  primary={primary}
                />
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* ── PRODUCTOS ───────────────────────────────────────────────────────── */}
      {hasProductos && (
        <section
          id="productos"
          aria-label="Productos"
          className={`${getSectionBg(
            categorias.length + postCatOffset.productos
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 overflow-hidden`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8 sm:mb-10 gap-4 flex-wrap">
              <div>
                <p
                  className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2"
                  style={{ color: primary }}
                >
                  Catálogo
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                  {t('companyDetail.tabProducts')}
                </h2>
                <div
                  className="w-12 h-px mt-4"
                  style={{ backgroundColor: primary }}
                />
              </div>
              <span className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-gray-500 font-medium">
                {empresa.productos.length} productos
              </span>
            </div>

            <MasonryGrid
              items={productosVisibles}
              variant="contain"
              onClick={(item) => setProductoActivo(item._orig)}
              renderBadge={(item) =>
                item.precio != null ? (
                  <div className="absolute top-3 right-3 z-10 bg-white/90 dark:bg-black/90 backdrop-blur-sm text-[#0f172a] dark:text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
                    {formatPrecio(item.precio)}€
                  </div>
                ) : null
              }
              renderHoverInfo={(item) => (
                <>
                  <p className="text-white text-sm font-semibold leading-tight drop-shadow-md">
                    {item.alt}
                  </p>
                  {item.descripcion && (
                    <p className="text-white/70 text-xs mt-1 line-clamp-2">
                      {item.descripcion}
                    </p>
                  )}
                </>
              )}
            />

            {productoItems.length > 8 && (
              <div className="flex justify-center mt-8 sm:mt-10">
                <button
                  onClick={() => setVerMasProductos((v) => !v)}
                  className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase px-6 sm:px-8 py-3 rounded-full transition-all duration-300"
                  style={{
                    color: primary,
                    border: `1px solid ${hexToRgba(primary, 0.3)}`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      primary;
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = hexToRgba(primary, 0.06);
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      hexToRgba(primary, 0.3);
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = 'transparent';
                  }}
                >
                  {verMasProductos
                    ? 'Ver menos'
                    : `Ver catálogo completo (${productoItems.length})`}
                  <ChevronRight
                    className={`w-3.5 h-3.5 transition-transform duration-300 ${
                      verMasProductos ? 'rotate-90' : ''
                    }`}
                  />
                </button>
              </div>
            )}
          </motion.div>
        </section>
      )}

      {/* ── EQUIPO ──────────────────────────────────────────────────────────── */}
      {hasTrabajadores && (
        <section
          id="equipo"
          aria-label="Equipo"
          className={`${getSectionBg(
            categorias.length + postCatOffset.equipo
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                {t('companyDetail.tabTeam')}
              </h2>
              <div
                className="w-12 h-px mt-4 sm:mt-5"
                style={{ backgroundColor: primary }}
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
              {empresa.trabajadores.map((trabajador, idx) => (
                <motion.div
                  key={trabajador.id_usuario}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: idx * 0.07 }}
                  className="group relative overflow-hidden rounded-2xl sm:rounded-3xl bg-[#f7f5f3] dark:bg-white/5 cursor-default"
                >
                  <div className="aspect-[3/4] relative overflow-hidden">
                    {trabajador.foto ? (
                      <img
                        src={trabajador.foto}
                        alt={trabajador.nombre}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${hexToRgba(
                            primary,
                            0.15
                          )}, ${hexToRgba(primary, 0.25)})`,
                        }}
                      >
                        <span
                          className="text-4xl sm:text-5xl font-light select-none"
                          style={{ color: hexToRgba(primary, 0.6) }}
                        >
                          {trabajador.nombre?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                      <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-white leading-tight">
                        {trabajador.nombre}
                      </h3>
                    </div>
                  </div>
                  <div
                    className="h-0.5 w-0 group-hover:w-full transition-all duration-500 ease-out"
                    style={{ backgroundColor: primary }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* ── GALERÍA ─────────────────────────────────────────────────────────── */}
      {hasFotos && (
        <section
          id="galeria"
          aria-label="Galería de fotos"
          className={`${getSectionBg(
            categorias.length + postCatOffset.galeria
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10 sm:mb-16 flex-wrap gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                  {t('companyDetail.tabGallery')}
                </h2>
                <div
                  className="w-12 h-px mt-4 sm:mt-5"
                  style={{ backgroundColor: primary }}
                />
              </div>
              <span className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-gray-500 font-medium">
                {empresa.fotos.length}{' '}
                {empresa.fotos.length === 1 ? 'foto' : 'fotos'}
              </span>
            </div>

            <MasonryGrid
              items={fotosVisibles}
              onClick={(item) => setFotoActiva(item._orig)}
            />

            {fotoItems.length > 8 && (
              <div className="flex justify-center mt-8 sm:mt-10">
                <button
                  onClick={() => setVerTodasFotos((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold px-6 sm:px-8 py-3 rounded-full transition-all duration-300"
                  style={{
                    color: primary,
                    border: `1px solid ${hexToRgba(primary, 0.3)}`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      primary;
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = hexToRgba(primary, 0.06);
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      hexToRgba(primary, 0.3);
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = 'transparent';
                  }}
                >
                  {verTodasFotos
                    ? 'Ver menos'
                    : `Ver todas las fotos (${fotoItems.length})`}
                  <ChevronRight
                    className={`w-4 h-4 transition-transform duration-300 ${
                      verTodasFotos ? 'rotate-90' : ''
                    }`}
                  />
                </button>
              </div>
            )}
          </motion.div>

          {/* Lightbox */}
          <Dialog open={!!fotoActiva} onOpenChange={() => setFotoActiva(null)}>
            <DialogContent className="max-w-5xl w-[95vw] p-0 bg-black/95 border border-white/15 rounded-2xl overflow-hidden [&>button:last-child]:hidden">
              <DialogHeader className="sr-only">
                <DialogTitle>{fotoActiva?.titulo || 'Foto'}</DialogTitle>
              </DialogHeader>

              {/* Cerrar (visible sobre fondo oscuro; la X por defecto queda oculta) */}
              <button
                type="button"
                onClick={() => setFotoActiva(null)}
                aria-label="Cerrar"
                className="absolute right-3 top-3 z-20 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {fotoActiva && (
                <div className="relative w-full h-[80vh] flex items-center justify-center">
                  <img
                    src={fotoActiva.url_foto}
                    alt={fotoActiva.titulo || 'Foto'}
                    className="max-w-full max-h-full object-contain"
                  />
                  {fotosOrdenadas.length > 1 &&
                    (() => {
                      const currentIdx = fotosOrdenadas.findIndex(
                        (f) => f.id_foto === fotoActiva.id_foto
                      );
                      const prev =
                        fotosOrdenadas[
                          (currentIdx - 1 + fotosOrdenadas.length) %
                            fotosOrdenadas.length
                        ];
                      const next =
                        fotosOrdenadas[
                          (currentIdx + 1) % fotosOrdenadas.length
                        ];
                      return (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFotoActiva(prev);
                            }}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center transition-all"
                          >
                            <ChevronRight className="w-5 h-5 text-white rotate-180" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFotoActiva(next);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center transition-all"
                          >
                            <ChevronRight className="w-5 h-5 text-white" />
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {fotosOrdenadas.map((f) => (
                              <button
                                key={f.id_foto}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFotoActiva(f);
                                }}
                                className={`h-1.5 rounded-full transition-all ${
                                  f.id_foto === fotoActiva.id_foto
                                    ? 'bg-white w-4'
                                    : 'bg-white/40 w-1.5'
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      );
                    })()}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </section>
      )}

      {/* ── UBICACIÓN ───────────────────────────────────────────────────────── */}
      {hasEncuentranos && (
        <section
          id="ubicacion"
          aria-label="Horarios y ubicación"
          className={`${getSectionBg(
            categorias.length + postCatOffset.ubicacion
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                Horarios y Ubicación
              </h2>
              <div
                className="w-12 h-px mt-4 sm:mt-5"
                style={{ backgroundColor: primary }}
              />
            </div>

            <div className="grid lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 items-stretch">
              <div className="lg:col-span-2 flex flex-col gap-4 sm:gap-5">
                {/* Horario */}
                {empresa.horario_empresa.length > 0 &&
                  (() => {
                    const todayIdx = new Date().getDay();
                    const MAP: Record<string, number> = {
                      Lun: 1,
                      Mar: 2,
                      Mié: 3,
                      Jue: 4,
                      Vie: 5,
                      Sáb: 6,
                      Dom: 0,
                    };
                    const isTodayRow = (label: string) =>
                      Object.entries(MAP).some(
                        ([short, idx]) =>
                          label.includes(short) && idx === todayIdx
                      );
                    return (
                      <div className="bg-white dark:bg-white/5 rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 dark:border-white/10 shadow-sm">
                        <div className="flex items-center gap-3 mb-4 sm:mb-5">
                          <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                            style={{
                              backgroundColor: hexToRgba(primary, 0.12),
                            }}
                          >
                            <Clock
                              className="w-4 h-4"
                              style={{ color: primary }}
                            />
                          </div>
                          <h4 className="font-semibold text-sm tracking-wide text-[#0f172a] dark:text-white uppercase">
                            Horario
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {scheduleRows.map((row, i) => {
                            const isToday = isTodayRow(row.label);
                            return (
                              <div
                                key={i}
                                className="flex items-center justify-between px-3 py-2 rounded-xl transition-colors hover:bg-gray-50 dark:hover:bg-white/10"
                                style={
                                  isToday
                                    ? {
                                        backgroundColor: hexToRgba(
                                          primary,
                                          0.08
                                        ),
                                      }
                                    : {}
                                }
                              >
                                <div className="flex items-center gap-2">
                                  {isToday && (
                                    <span
                                      className="w-1.5 h-1.5 rounded-full shrink-0"
                                      style={{ backgroundColor: primary }}
                                    />
                                  )}
                                  <span
                                    className={`text-sm w-20 shrink-0 ${
                                      isToday
                                        ? 'font-semibold'
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`}
                                    style={isToday ? { color: primary } : {}}
                                  >
                                    {row.label}
                                    {isToday && (
                                      <span className="ml-1 text-[10px] font-bold tracking-wide opacity-70">
                                        HOY
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {row.closed ? (
                                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                                    Cerrado
                                  </span>
                                ) : (
                                  <div className="flex flex-col items-end gap-0.5">
                                    {row.slots.map((s, j) => (
                                      <span
                                        key={j}
                                        className={`text-sm tabular-nums ${
                                          isToday
                                            ? 'font-semibold text-[#0f172a] dark:text-white'
                                            : 'text-gray-700 dark:text-gray-300'
                                        }`}
                                      >
                                        {s}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                {/* Dirección */}
                {direccionCompleta && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white dark:bg-white/5 rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300 block"
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.borderColor =
                        hexToRgba(primary, 0.3);
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.borderColor =
                        '';
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: hexToRgba(primary, 0.12) }}
                      >
                        <MapPin
                          className="w-4 h-4"
                          style={{ color: primary }}
                        />
                      </div>
                      <h4 className="font-semibold text-sm tracking-wide text-[#0f172a] dark:text-white uppercase">
                        Dirección
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed pl-12">
                      {[
                        empresa.direccion,
                        empresa.localidad,
                        empresa.ciudad,
                        empresa.pais,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                    <div className="pl-12 mt-3">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: primary }}
                      >
                        Ver en Google Maps <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </a>
                )}

                {/* Contacto + redes */}
                {hasContacto && (
                  <div className="bg-white dark:bg-white/5 rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-gray-100 dark:border-white/10 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: hexToRgba(primary, 0.12) }}
                      >
                        <Phone className="w-4 h-4" style={{ color: primary }} />
                      </div>
                      <h4 className="font-semibold text-sm tracking-wide text-[#0f172a] dark:text-white uppercase">
                        Contacto
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-12">
                      {empresa.telefono && (
                        <a
                          href={`tel:${empresa.telefono}`}
                          className="inline-flex items-center gap-1.5 bg-[#f6f7f9] dark:bg-white/5 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200"
                          onMouseEnter={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.backgroundColor = hexToRgba(primary, 0.1);
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              primary;
                          }}
                          onMouseLeave={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.backgroundColor = '';
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              '';
                          }}
                        >
                          <Phone className="w-3 h-3" />
                          {empresa.telefono}
                        </a>
                      )}
                      {empresa.email && (
                        <a
                          href={`mailto:${empresa.email}`}
                          className="inline-flex items-center gap-1.5 bg-[#f6f7f9] dark:bg-white/5 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200 max-w-full truncate"
                          onMouseEnter={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.backgroundColor = hexToRgba(primary, 0.1);
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              primary;
                          }}
                          onMouseLeave={(e) => {
                            (
                              e.currentTarget as HTMLAnchorElement
                            ).style.backgroundColor = '';
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              '';
                          }}
                        >
                          <MapPin className="w-3 h-3 shrink-0" />
                          {empresa.email}
                        </a>
                      )}
                      <SocialLinks
                        redes={redes}
                        variant="contact"
                        primary={primary}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Mapa */}
              <div className="lg:col-span-3 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-100 dark:border-white/10 shadow-sm min-h-[280px] sm:min-h-[360px] relative">
                {direccionCompleta ? (
                  <>
                    {mapaActivo ? (
                    <iframe
                      title="Ubicación"
                      width="100%"
                      height="100%"
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 0, minHeight: '280px' }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(
                        direccionCompleta
                      )}&output=embed&z=15`}
                    />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#f0f4f8] dark:bg-white/5">
                        <MapPin className="w-10 h-10 text-gray-300 dark:text-gray-500" />
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                          {t('cookies.mapConsentText')}
                        </p>
                        <button
                          type="button"
                          onClick={() => setMapaActivo(true)}
                          className="text-xs font-bold tracking-[0.15em] uppercase px-4 py-2 rounded-full border border-gray-300 dark:border-white/20 text-[#0f172a] dark:text-white hover:bg-white dark:hover:bg-white/10 transition-colors"
                        >
                          {t('cookies.mapConsentButton')}
                        </button>
                      </div>
                    )}
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-4 right-4 z-10 flex items-center gap-2 bg-white dark:bg-black text-[#0f172a] dark:text-white text-xs font-bold tracking-[0.15em] uppercase px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-lg transition-all duration-200"
                      onMouseEnter={(e) => {
                        (
                          e.currentTarget as HTMLAnchorElement
                        ).style.backgroundColor = primary;
                        (e.currentTarget as HTMLAnchorElement).style.color =
                          '#fff';
                      }}
                      onMouseLeave={(e) => {
                        (
                          e.currentTarget as HTMLAnchorElement
                        ).style.backgroundColor = '';
                        (e.currentTarget as HTMLAnchorElement).style.color = '';
                      }}
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      Abrir en Maps
                    </a>
                  </>
                ) : (
                  <div className="w-full h-full bg-[#f0f4f8] dark:bg-white/5 flex items-center justify-center min-h-[280px] sm:min-h-[360px]">
                    <MapPin className="w-10 h-10 text-gray-300 dark:text-gray-500" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* ── VALORACIONES ────────────────────────────────────────────────────── */}
      {(empresa.valoraciones_empresa?.length ?? 0) > 0 && (
        <section
          id="valoraciones"
          aria-label="Opiniones y valoraciones"
          className={`${getSectionBg(
            categorias.length + postCatOffset.valoraciones
          )} ${SCROLL_MARGIN} py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16`}
        >
          <motion.div {...fadeUp} className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 sm:gap-10 mb-10 sm:mb-16">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#0f172a] dark:text-white tracking-wide">
                  {t('companyDetail.tabRatings')}
                </h2>
                <div
                  className="w-12 h-px mt-4 sm:mt-5"
                  style={{ backgroundColor: primary }}
                />
              </div>
              {empresa.valoracion_media_empresa && (
                <div className="flex items-center gap-6 sm:gap-8 bg-white dark:bg-white/5 rounded-2xl sm:rounded-3xl px-5 sm:px-8 py-5 sm:py-6 border border-gray-100 dark:border-white/10 shadow-sm shrink-0 w-full lg:w-auto">
                  <div className="text-center">
                    <p className="text-5xl sm:text-6xl font-light text-[#0f172a] dark:text-white leading-none tabular-nums">
                      {empresa.valoracion_media_empresa.toFixed(1)}
                    </p>
                    <div className="flex gap-0.5 mt-2 justify-center">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.round(empresa.valoracion_media_empresa!)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-200 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 tracking-wide">
                      {empresa.total_valoraciones_empresa ??
                        empresa.valoraciones_empresa?.length}{' '}
                      opiniones
                    </p>
                  </div>
                  <div className="space-y-2 flex-1 min-w-[140px] sm:min-w-[160px]">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count =
                        empresa.valoraciones_empresa?.filter(
                          (v: any) => v.puntuacion === star
                        ).length ?? 0;
                      const pct = Math.round(
                        (count / (empresa.valoraciones_empresa?.length ?? 1)) *
                          100
                      );
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 w-2 tabular-nums shrink-0">
                            {star}
                          </span>
                          <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400 shrink-0" />
                          <div className="flex-1 h-1.5 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              whileInView={{ width: `${pct}%` }}
                              viewport={{ once: true }}
                              transition={{
                                duration: 0.8,
                                delay: (5 - star) * 0.08,
                              }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: primary }}
                            />
                          </div>
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 w-5 text-right tabular-nums shrink-0">
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {(() => {
              const reviews = valoracionesVisibles;
              const cols: (typeof reviews)[] = [[], [], []];
              reviews.forEach((v: any, i: number) => cols[i % 3].push(v));
              return (
                <>
                  <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 items-start">
                    {cols.map((col, colIdx) => (
                      <div
                        key={colIdx}
                        className={`flex flex-col gap-3 sm:gap-4 ${
                          colIdx === 1 ? 'mt-6' : ''
                        } ${colIdx === 2 ? 'hidden lg:flex' : ''}`}
                      >
                        {col.map((v: any, idx: number) => (
                          <ReviewCard
                            key={v.id_valoracion}
                            v={v}
                            idx={colIdx * 10 + idx}
                            primary={primary}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="md:hidden flex flex-col gap-3">
                    {reviews.map((v: any, idx: number) => (
                      <ReviewCard
                        key={v.id_valoracion}
                        v={v}
                        idx={idx}
                        primary={primary}
                      />
                    ))}
                  </div>
                </>
              );
            })()}

            {(empresa.valoraciones_empresa?.length ?? 0) >
              valoracionesVisibles.length && (
              <div className="flex justify-center mt-8 sm:mt-10">
                <button
                  onClick={() => setMostrarTodas(true)}
                  className="flex items-center gap-2 text-sm font-semibold px-6 sm:px-8 py-3 rounded-full transition-all duration-300"
                  style={{
                    color: primary,
                    border: `1px solid ${hexToRgba(primary, 0.3)}`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      primary;
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = hexToRgba(primary, 0.06);
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      hexToRgba(primary, 0.3);
                    (
                      e.currentTarget as HTMLButtonElement
                    ).style.backgroundColor = 'transparent';
                  }}
                >
                  {t('companyDetail.seeAllRatings')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>

          <Dialog open={mostrarTodas} onOpenChange={setMostrarTodas}>
            <DialogContent className="w-[95%] sm:max-w-2xl mx-auto rounded-2xl sm:rounded-3xl border border-gray-100 dark:border-white/10 bg-white dark:bg-black p-0 max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader className="px-5 sm:px-7 pt-5 sm:pt-7 pb-4 border-b border-gray-100 dark:border-white/10 shrink-0">
                <DialogTitle className="text-xl font-light text-[#0f172a] dark:text-white tracking-wide">
                  {t('companyDetail.ratingsTitle', { name: empresa.empresa })}
                </DialogTitle>
                {empresa.valoracion_media_empresa && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${
                            i < Math.round(empresa.valoracion_media_empresa!)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-200 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-[#0f172a] dark:text-white">
                      {empresa.valoracion_media_empresa.toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      · {empresa.valoraciones_empresa?.length} opiniones
                    </span>
                  </div>
                )}
              </DialogHeader>
              <div className="overflow-y-auto px-5 sm:px-7 py-4 sm:py-5 flex flex-col gap-3">
                {empresa.valoraciones_empresa?.map((v: any, idx: number) => (
                  <ReviewCard
                    key={v.id_valoracion}
                    v={v}
                    idx={idx}
                    primary={primary}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </section>
      )}
      <ScrollToTopButton primary={primary} />

      <ProductDialog
        producto={productoActivo}
        onClose={() => setProductoActivo(null)}
      />
      <Footer />
    </div>
  );
};

export default CompanyDetail;